import React from 'react';
import { Routes, Route, Link, useNavigate } from 'react-router-dom';
import { ErrorBoundary } from '@ohif/ui-next';

// Route Components
// Study list variants are selected by the `workList.variant` customization:
// - 'legacy'  -> LegacyWorkList (the pre-3.13 study list; deprecated, removed in a future release)
// - anything else (including 'default') -> WorkList (ui-next study list)
import WorkList from './WorkList/WorkList';
import DataSourceWrapper from './DataSourceWrapper';
import Local from './Local';
import Debug from './Debug';
import NotFound from './NotFound';
import buildModeRoutes from './buildModeRoutes';
import PrivateRoute from './PrivateRoute';
import { routerBasename } from '../utils/publicUrl';
import { useAppConfig } from '@state';
import { history } from '../utils/history';

// The legacy list is its own chunk, fetched only when the variant selects it, so
// deployments on the default list do not download @ohif/ui or react-select.
const LazyLegacyWorkList = React.lazy(() => import('./LegacyWorkList/LegacyWorkList'));
const LegacyWorkList = (props: React.ComponentProps<typeof LazyLegacyWorkList>) => (
  <React.Suspense fallback={null}>
    <LazyLegacyWorkList {...props} />
  </React.Suspense>
);

const NotFoundServer = ({
  message = 'Unable to query for studies at this time. Check your data source configuration or network connection',
}) => {
  return (
    <div className="text-foreground absolute flex h-full w-full items-center justify-center">
      <div>
        <h4>{message}</h4>
      </div>
    </div>
  );
};

const NotFoundStudy = () => {
  const [appConfig] = useAppConfig();
  const { showStudyList } = appConfig;

  return (
    <div className="text-foreground absolute flex h-full w-full items-center justify-center">
      <div>
        <h4 data-cy="study-not-found-message">
          One or more of the requested studies are not available at this time.
        </h4>
        {showStudyList && (
          <p
            className="mt-2"
            data-cy="return-to-study-list-message"
          >
            Return to the{' '}
            <Link
              className="text-highlight"
              to="/"
            >
              study list
            </Link>{' '}
            to select a different study to view.
          </p>
        )}
      </div>
    </div>
  );
};

// TODO: Include "routes" debug route if dev build
const bakedInRoutes = [
  {
    path: `/notfoundserver`,
    children: NotFoundServer,
  },
  {
    path: `/notfoundstudy`,
    children: NotFoundStudy,
  },
  {
    path: `/debug`,
    children: Debug,
  },
  {
    path: `/local`,
    children: Local.bind(null, { modePath: '' }), // navigate to the worklist
  },
  {
    path: `/localbasic`,
    children: Local.bind(null, { modePath: 'viewer/dicomlocal' }),
  },
];

// NOT FOUND (404)
const notFoundRoute = { path: '*', children: NotFound };

const createRoutes = ({
  modes,
  dataSources,
  extensionManager,
  servicesManager,
  commandsManager,
  hotkeysManager,
  showStudyList,
}: withAppTypes) => {
  const routes =
    buildModeRoutes({
      modes,
      dataSources,
      extensionManager,
      servicesManager,
      commandsManager,
      hotkeysManager,
    }) || [];

  const { customizationService } = servicesManager.services;

  const path =
    routerBasename.length > 1 && routerBasename.endsWith('/')
      ? routerBasename.substring(0, routerBasename.length - 1)
      : routerBasename;

  console.log('Registering worklist route', routerBasename, path);

  const workListVariant = customizationService.getCustomization('workList.variant');
  const WorkListComponent = workListVariant === 'legacy' ? LegacyWorkList : WorkList;

  const WorkListRoute = {
    path: '/',
    children: DataSourceWrapper,
    private: true,
    props: { children: WorkListComponent, servicesManager, extensionManager, commandsManager },
  };

  const customRoutes = customizationService.getCustomization('routes.customRoutes');

  const allRoutes = [
    ...routes,
    ...(showStudyList ? [WorkListRoute] : []),
    ...(customRoutes?.routes || []),
    ...bakedInRoutes,
    customRoutes?.notFoundRoute || notFoundRoute,
  ];

  function RouteWithErrorBoundary({ route, ...rest }) {
    const [appConfig] = useAppConfig();
    const { showErrorDetails } = appConfig;

    const navigate = useNavigate();

    // Assigned in an effect, not during render: history is a module singleton and
    // writing to it mid-render is an external mutation the compiler refuses. Its
    // setter takes only the first value it is given, and the sole consumer calls
    // history.navigate() from a command, well after mount.
    React.useEffect(() => {
      history.navigate = navigate;
    }, [navigate]);

    // eslint-disable-next-line react/jsx-props-no-spreading
    return (
      <ErrorBoundary
        context={`Route ${route.path}`}
        showErrorDetails={showErrorDetails}
      >
        <route.children
          {...rest}
          {...route.props}
          route={route}
          servicesManager={servicesManager}
          extensionManager={extensionManager}
          hotkeysManager={hotkeysManager}
        />
      </ErrorBoundary>
    );
  }

  const { userAuthenticationService } = servicesManager.services;

  // All routes are private by default and then we let the user auth service
  // to check if it is enabled or not
  // Todo: I think we can remove the second public return below
  return (
    <Routes>
      {allRoutes.map((route, i) => {
        return route.private === true ? (
          <Route
            key={i}
            path={route.path}
            element={
              <PrivateRoute
                handleUnauthenticated={() => userAuthenticationService.handleUnauthenticated()}
              >
                <RouteWithErrorBoundary route={route} />
              </PrivateRoute>
            }
          ></Route>
        ) : (
          <Route
            key={i}
            path={route.path}
            element={<RouteWithErrorBoundary route={route} />}
          />
        );
      })}
    </Routes>
  );
};

export default createRoutes;
